import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, AlertTriangle, Eraser } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LANGS = [
  { code: "ro", label: "🇷🇴 Română" }, { code: "en", label: "🇬🇧 English" },
  { code: "fr", label: "🇫🇷 Français" }, { code: "de", label: "🇩🇪 Deutsch" },
  { code: "es", label: "🇪🇸 Español" }, { code: "it", label: "🇮🇹 Italiano" },
  { code: "pt", label: "🇵🇹 Português" }, { code: "pt-BR", label: "🇧🇷 Português (Brasil)" },
  { code: "sr", label: "🇷🇸 Srpski" },
];

const LegalText = ({ text }) => (
  <div style={{ fontSize: "0.86rem", lineHeight: 1.65, color: "#2c2a26" }}>
    {text.split("\n\n").map((block, i) => {
      if (block.startsWith("## ")) {
        const [head, ...rest] = block.split("\n");
        return (
          <React.Fragment key={i}>
            <h4 style={{ fontFamily: "Cinzel, serif", fontSize: "0.9rem", color: "#0d1b2a", margin: "14px 0 4px" }}>{head.slice(3)}</h4>
            {rest.length > 0 && <p style={{ margin: "6px 0" }}>{rest.join(" ")}</p>}
          </React.Fragment>
        );
      }
      return <p key={i} style={{ margin: "6px 0" }}>{block}</p>;
    })}
  </div>
);

const Shell = ({ children }) => (
  <div style={{ minHeight: "100vh", background: "#0d1b2a", padding: "18px 12px 40px" }}>
    <div style={{ maxWidth: 760, margin: "0 auto", background: "#f7f3e8", borderRadius: 12, border: "1px solid #C8A96A", padding: "22px 18px" }}>
      {children}
    </div>
  </div>
);

const SignaturePad = ({ canvasRef, onDraw }) => {
  const drawing = useRef(false);
  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    return { x: ((e.clientX - r.left) * c.width) / r.width, y: ((e.clientY - r.top) * c.height) / r.height };
  };
  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    canvasRef.current.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2.6;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#14204a";
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    onDraw();
  };
  const end = () => { drawing.current = false; };
  return (
    <canvas
      ref={canvasRef}
      width={700}
      height={240}
      data-testid="consent-signature-canvas"
      style={{ width: "100%", height: 170, background: "#fdfbf5", border: "1.5px dashed #9a7530", borderRadius: 8, touchAction: "none", display: "block" }}
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={end}
      onPointerLeave={end}
    />
  );
};

export default function ConsentPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [lang, setLang] = useState(() => { const n = String(navigator.language || "ro"); return LANGS.some((l) => l.code === n) ? n : n.slice(0, 2); });
  const [accepted, setAccepted] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [formErr, setFormErr] = useState("");
  const canvasRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/public/consent/${token}`)
      .then(({ data: d }) => {
        setData(d);
        if (d.status === "signed") setDone(true);
      })
      .catch(() => setErr("invalid"));
  }, [token]);

  const activeLang = data?.texts?.[lang] ? lang : "ro";
  const t = data?.texts?.[activeLang];

  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  };

  const submit = async () => {
    setFormErr("");
    if (!accepted) { setFormErr(t.must_accept); return; }
    if (!hasInk) { setFormErr(t.must_sign); return; }
    setBusy(true);
    try {
      const png = canvasRef.current.toDataURL("image/png");
      await axios.post(`${API}/public/consent/${token}/sign`, { lang: activeLang, signature: png });
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (e) {
      setFormErr(e?.response?.data?.detail || String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  if (err) {
    return <Shell><div style={{ textAlign: "center", padding: "3rem 0", color: "#8a2626" }}><AlertTriangle size={34} style={{ margin: "0 auto 10px" }} /><p>Link invalid / Invalid link.</p></div></Shell>;
  }
  if (!data) {
    return <Shell><div style={{ textAlign: "center", padding: "4rem 0", color: "#9a7530" }}><Loader2 className="animate-spin" size={28} style={{ margin: "0 auto" }} /></div></Shell>;
  }
  if (done) {
    return (
      <Shell>
        <div style={{ textAlign: "center", padding: "3rem 0" }} data-testid="consent-success">
          <CheckCircle2 size={42} color="#3e6a3c" style={{ margin: "0 auto 12px" }} />
          <h2 style={{ fontFamily: "Cinzel, serif", color: "#0d1b2a" }}>{t.success_title}</h2>
          <p style={{ color: "#4c4a44", fontSize: "0.92rem", maxWidth: 460, margin: "10px auto 0" }}>
            {data.status === "signed" && !busy ? t.already_signed : t.success_body}
          </p>
        </div>
      </Shell>
    );
  }
  if (data.status === "expired" || data.status === "superseded") {
    return <Shell><div style={{ textAlign: "center", padding: "3rem 0", color: "#8a6f1c" }}><AlertTriangle size={34} style={{ margin: "0 auto 10px" }} /><p style={{ maxWidth: 420, margin: "0 auto" }}>{t.expired}</p></div></Shell>;
  }

  return (
    <Shell>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }} data-testid="consent-lang-selector">
        {LANGS.map((l) => (
          <button key={l.code} type="button" onClick={() => setLang(l.code)}
            data-testid={`consent-lang-${l.code}`}
            style={{
              padding: "5px 11px", borderRadius: 999, fontSize: "0.72rem", fontWeight: 700, cursor: "pointer",
              border: `1px solid ${activeLang === l.code ? "#0d1b2a" : "#c9bd9c"}`,
              background: activeLang === l.code ? "#0d1b2a" : "transparent",
              color: activeLang === l.code ? "#e8d59a" : "#6b6a65",
            }}>
            {l.label}
          </button>
        ))}
      </div>

      <h1 style={{ fontFamily: "Cinzel, serif", fontSize: "1.35rem", color: "#0d1b2a", margin: "0 0 4px" }}>{t.page_title}</h1>
      <p style={{ color: "#4c4a44", fontSize: "0.9rem", margin: "0 0 4px", fontWeight: 600 }}>{t.hello.replace("{name}", data.member_name)}</p>
      <p style={{ color: "#4c4a44", fontSize: "0.88rem", margin: "0 0 16px" }}>{t.intro.replace("{lodge_name}", data.lodge_name)}</p>

      <div style={{ maxHeight: 340, overflowY: "auto", background: "#fdfbf5", border: "1px solid #d8cdaa", borderRadius: 8, padding: "12px 16px" }} data-testid="consent-legal-scroll">
        <h3 style={{ fontFamily: "Cinzel, serif", fontSize: "1.02rem", color: "#9a7530", margin: "4px 0 8px" }}>{t.terms_title}</h3>
        <LegalText text={t.terms} />
        <h3 style={{ fontFamily: "Cinzel, serif", fontSize: "1.02rem", color: "#9a7530", margin: "20px 0 8px" }}>{t.gdpr_title}</h3>
        <LegalText text={t.gdpr} />
      </div>

      <label style={{ display: "flex", gap: 10, alignItems: "flex-start", margin: "16px 0", cursor: "pointer" }}>
        <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
          data-testid="consent-accept-checkbox" style={{ marginTop: 3, width: 17, height: 17, accentColor: "#0d1b2a" }} />
        <span style={{ fontSize: "0.84rem", color: "#2c2a26", lineHeight: 1.55 }}>{t.accept_label}</span>
      </label>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div>
          <div style={{ fontFamily: "Cinzel, serif", fontSize: "0.95rem", color: "#0d1b2a" }}>{t.sign_title}</div>
          <div style={{ fontSize: "0.76rem", color: "#8a8577" }}>{t.sign_hint}</div>
        </div>
        <button type="button" onClick={clear} data-testid="consent-clear-signature"
          style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "0.72rem", fontWeight: 700, color: "#8a2626", background: "none", border: "1px solid #c9a", borderRadius: 999, padding: "4px 10px", cursor: "pointer" }}>
          <Eraser size={12} /> {t.clear}
        </button>
      </div>
      <SignaturePad canvasRef={canvasRef} onDraw={() => setHasInk(true)} />

      {formErr && <p style={{ color: "#8a2626", fontSize: "0.82rem", marginTop: 10 }} data-testid="consent-form-error">{formErr}</p>}

      <button type="button" onClick={submit} disabled={busy} data-testid="consent-submit-btn"
        style={{
          width: "100%", marginTop: 16, padding: "14px 10px", borderRadius: 8, border: "none", cursor: "pointer",
          background: "#0d1b2a", color: "#e8d59a", fontWeight: 800, letterSpacing: "0.12em", fontSize: "0.82rem",
          opacity: busy ? 0.6 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}>
        {busy ? <Loader2 size={15} className="animate-spin" /> : null} {t.submit}
      </button>
    </Shell>
  );
}
